import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface TreatmentAcceptanceOverviewQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface TreatmentAcceptanceOverviewKpiData {
  txPresented: string
  presentedValue: string
  acceptedValue: string
  txCompletedActual: string
  txCompletedTotal: string
  primaryValue: number
  secondaryValue: number
}

export interface TreatmentAcceptanceOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  goals: null
  kpis: TreatmentAcceptanceOverviewKpiData
}

export interface TreatmentAcceptanceDetailKpiData {
  acceptedRate: string
  presentedValue: string
  acceptedValue: string
  sameDayValue: string
  comparisonLabel: string
  acceptedRateDelta: string
  acceptedRateTrend: 'up' | 'down'
  presentedDelta: string
  presentedTrend: 'up' | 'down'
  acceptedDelta: string
  acceptedTrend: 'up' | 'down'
  sameDayDelta: string
  sameDayTrend: 'up' | 'down'
}

export interface TreatmentAcceptanceDetailChartPoint {
  label: string
  presented: number
  accepted: number
  isCurrent?: boolean
}

export interface TreatmentAcceptanceDetailChartConfig {
  data: TreatmentAcceptanceDetailChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface TreatmentAcceptanceDetailByProviderRow {
  id: string
  provider: string
  presented: string
  accepted: string
  acceptanceRatePct: string
  sameDayAcceptance: string
  followUpAcceptance: string
}

export interface TreatmentAcceptanceDetailByCategoryRow {
  id: string
  category: string
  presented: string
  accepted: string
  acceptanceRate: string
  avgCaseValue: string
}

export interface TreatmentAcceptanceDetailByTimePeriodRow {
  id: string
  timePeriod: string
  presented: string
  accepted: string
  acceptanceRate: string
}

export interface TreatmentAcceptanceDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: TreatmentAcceptanceDetailKpiData
  chart: TreatmentAcceptanceDetailChartConfig
  byProvider: TreatmentAcceptanceDetailByProviderRow[]
  byCategory: TreatmentAcceptanceDetailByCategoryRow[]
  byTimePeriod: TreatmentAcceptanceDetailByTimePeriodRow[]
}

const treatmentAcceptanceGet = <T>(
  path: string,
  params: TreatmentAcceptanceOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/treatment/treatment-acceptance/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getTreatmentAcceptanceOverview = (
  params: TreatmentAcceptanceOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<TreatmentAcceptanceOverviewResult> =>
  treatmentAcceptanceGet('overview', params, options)

export const getTreatmentAcceptanceDetail = (
  params: TreatmentAcceptanceOverviewQueryParams,
  options?: { signal?: AbortSignal },
): Promise<TreatmentAcceptanceDetailResult> =>
  treatmentAcceptanceGet('detail', params, options)
